import { ChangeEvent, forwardRef } from 'react';
import { Radio, RadioProps, Stack, Typography, useTheme } from '@mui/material';

import {
	getRadioSxProps,
	StyledRadioButtonCircle,
	StyledRadioButtonInnerCircle,
	TRUNCATE_TEXT_SX_PROPS,
} from '../MplRadioButton/MplRadioButton.styled';

import {
	StyledDatesStack,
	StyledRadioButtonWithDatesContainer,
} from './MplRadioButtonWithDates.styled';

interface MplRadioButtonWithDatesProps
	extends Omit<RadioProps, 'icon' | 'checkedIcon'> {
	text: string;
	ticketValidUntilLabel: string;
	ticketValidUntilDate: string;
	ticketActiveFromLabel: string;
	ticketActiveFromDate: string;
	validityTextColor?: string;
	disabled?: boolean;
	compact?: boolean;
}

export const MplRadioButtonWithDates = forwardRef<
	HTMLButtonElement,
	MplRadioButtonWithDatesProps
>(
	(
		{
			text,
			ticketValidUntilLabel,
			ticketValidUntilDate,
			ticketActiveFromLabel,
			ticketActiveFromDate,
			validityTextColor,
			disabled = false,
			compact = false,
			...radioProps
		},
		ref
	) => {
		const theme = useTheme();

		const handleClick = () => {
			if (!disabled && radioProps.onChange) {
				const syntheticEvent = {
					target: { checked: true },
				} as ChangeEvent<HTMLInputElement>;
				radioProps.onChange(syntheticEvent, true);
			}
		};

		return (
			<StyledRadioButtonWithDatesContainer
				disabled={disabled}
				checked={radioProps.checked}
				compact={compact}
				onClick={handleClick}
			>
				<Stack flex={1} minWidth={0} justifyContent="center" marginRight="4px">
					<Typography
						variant="b2-m"
						color={theme.palette.primary.main}
						sx={TRUNCATE_TEXT_SX_PROPS}
					>
						{text}
					</Typography>
					<StyledDatesStack compact={compact}>
						<Stack direction="row" spacing={0.5} alignItems="baseline">
							<Typography variant="l1" color={theme.palette.solutions?.[600]}>
								{ticketValidUntilLabel}
							</Typography>
							<Typography variant="l1" color={theme.palette.primary.main}>
								{ticketValidUntilDate}
							</Typography>
						</Stack>
						<Stack direction="row" spacing={0.5} alignItems="baseline">
							<Typography variant="l1" color={theme.palette.solutions?.[600]}>
								{ticketActiveFromLabel}
							</Typography>
							<Typography
								variant="l1"
								color={validityTextColor ?? theme.palette.primary.main}
							>
								{ticketActiveFromDate}
							</Typography>
						</Stack>
					</StyledDatesStack>
				</Stack>

				<Radio
					ref={ref}
					disabled={disabled}
					{...radioProps}
					sx={getRadioSxProps(theme)}
					icon={<StyledRadioButtonCircle disabled={disabled} />}
					checkedIcon={
						<StyledRadioButtonCircle disabled={disabled}>
							<StyledRadioButtonInnerCircle disabled={disabled} />
						</StyledRadioButtonCircle>
					}
				/>
			</StyledRadioButtonWithDatesContainer>
		);
	}
);

MplRadioButtonWithDates.displayName = 'MplRadioButtonWithDates';
